import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import LoginPrompt from '../../Auth/LoginPrompt';
import ProUpgradeModal from '../../components/chat/ProUpgradeModal';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/common/ConfirmDialog';
import {
  getStyleProfiles,
  analyzeStyleSources,
  analyzeStyleFiles,
  upsertStyleProfile,
  deleteStyleProfile,
  setStyleMasterEnabled,
  getSaenggibuUsageSummary,
} from '../../api/saenggibuApi';
import { formatSaenggibuUsageLine } from '../../utils/saenggibuSuggestion';
import { trackSaenggibuUxEvent } from '../../utils/eventTracker';
import {
  FREE_STYLE_EXAMPLE_LIMIT,
  getStyleExamplesLimit,
  buildStyleProfileSavePayload,
  styleExampleRejectionFromError,
  createStyleExampleItems,
  mergePendingStyleExampleDraft,
} from '../../utils/saenggibuStyleExamples';
import { hasStyleProfileContent } from '../../utils/saenggibuStyleProfile';
import StyleExamplesSection from '../../components/saenggibu/StyleExamplesSection';
import { StyleApplyDemo } from '../../components/demo';
import './MyStylePage.css';

// scope → 화면 라벨 (생성 폼 칩 라벨과 동일하게 유지)
const SCOPE_LABELS = {
  global: '공통 설정',
  행동발달: '행동발달',
  교과: '교과/세특',
  교과_고등: '교과/세특(고등)',
  창체: '창체/자율',
  동아리: '동아리',
  진로: '진로',
};
const CATEGORY_SCOPES = ['행동발달', '교과', '교과_고등', '창체', '동아리', '진로'];

const ALLOWED_FILE_EXTENSIONS = [
  '.pdf', '.hwp', '.hwpx', '.docx', '.ppt', '.pptx', '.xls', '.xlsx',
  '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif',
];

const isAllowedStyleFile = (file) => {
  const name = String(file?.name || '').toLowerCase();
  return ALLOWED_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const EMPTY_DRAFT = {
  tone_style: '',
  sentence_endings: [],
  structure_flow: '',
  fixed_phrases: [],
  vocabulary_notes: '',
};

const splitExamples = (raw) =>
  String(raw || '')
    .split(/\n\s*\n\s*\n|\n\s*---+\s*\n/)
    .map((t) => t.trim())
    .filter(Boolean);

const profileSummary = (profile) => {
  if (!profile) return null;
  const parts = [];
  if (profile.source_texts_count > 0) parts.push(`예시 ${profile.source_texts_count}개`);
  if ((profile.rule_summary || []).length > 0) parts.push(`규칙 ${profile.rule_summary.length}개`);
  if ((profile.instructions || '').trim()) parts.push('지시 있음');
  // 참고 예시만 있어도 생성에 적용된다(백엔드 profile_has_content) — "설정 안 함"으로 보이지 않게 한다
  const examplesCount = Array.isArray(profile.examples) ? profile.examples.length : Number(profile.examples_count) || 0;
  if (examplesCount > 0) parts.push(`참고 예시 ${examplesCount}개`);
  if (parts.length === 0 && profile.style_spec && Object.keys(profile.style_spec).length > 0) {
    parts.push('스타일 분석됨');
  }
  return parts.join(' · ') || null;
};

const MyStylePage = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const requestConfirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState({});
  const [masterEnabled, setMasterEnabled] = useState(true);
  const [masterToggling, setMasterToggling] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [freeAnalyzeUsed, setFreeAnalyzeUsed] = useState(0);
  const [freeAnalyzeLimit, setFreeAnalyzeLimit] = useState(1);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showProModal, setShowProModal] = useState(false);

  // 편집 모달 상태
  const [editScope, setEditScope] = useState(null); // null = 닫힘
  const [exampleText, setExampleText] = useState('');
  const [styleFiles, setStyleFiles] = useState([]);
  const [extractedTexts, setExtractedTexts] = useState(null); // 파일 분석 시 추출 원문
  const [instructions, setInstructions] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [draftRules, setDraftRules] = useState([]);
  const [analysisModel, setAnalysisModel] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingScope, setTogglingScope] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [exampleItems, setExampleItems] = useState([]);
  const [exampleDraft, setExampleDraft] = useState('');
  const [examplesTouched, setExamplesTouched] = useState(false);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [exampleReject, setExampleReject] = useState(null);
  const [saveWarnings, setSaveWarnings] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [quickText, setQuickText] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);

  // Free는 공통 스타일 1개를 저장·적용하고, 종류별 설정은 PRO에서 쓴다 (백엔드 scope_allowed 와 같은 규칙).
  const canEditScope = (scope) => isPro || scope === 'global';

  const addStyleFiles = useCallback((picked) => {
    const files = Array.from(picked || []);
    if (files.length === 0) return;
    const allowed = files.filter(isAllowedStyleFile);
    if (allowed.length < files.length) {
      showToast('PDF, HWP, PPT, 엑셀, 이미지 파일만 올릴 수 있어요', 'warning');
    }
    if (allowed.length > 0) {
      setStyleFiles((cur) => [...cur, ...allowed].slice(0, 5));
    }
  }, [showToast]);

  const loadProfiles = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await getStyleProfiles();
      setProfiles(data.profiles || {});
      setMasterEnabled(data.master_enabled !== false);
      setIsPro(Boolean(data.is_pro));
      setFreeAnalyzeUsed(data.free_analyze_used || 0);
      setFreeAnalyzeLimit(data.free_analyze_limit ?? 1);
    } catch (error) {
      console.error('스타일 설정 조회 실패:', error);
      showToast('스타일 설정을 불러오지 못했어요', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser, showToast]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (!currentUser) {
      setUsageSummary(null);
      return undefined;
    }
    let cancelled = false;
    getSaenggibuUsageSummary()
      .then((data) => {
        if (!cancelled) setUsageSummary(data || null);
      })
      .catch(() => {
        if (!cancelled) setUsageSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const openEditor = (scope) => {
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    if (!canEditScope(scope)) {
      setShowProModal(true);
      return;
    }
    const existing = profiles[scope];
    setEditScope(scope);
    setExampleText('');
    setStyleFiles([]);
    setExtractedTexts(null);
    setInstructions(existing?.instructions || '');
    setDraft({ ...EMPTY_DRAFT, ...(existing?.style_spec || {}) });
    setDraftRules(existing?.rule_summary || []);
    setAnalysisModel(existing?.analysis_model || '');
    setExampleItems((existing?.examples || []).map((item, index) => ({
      localKey: item.id || `loaded-${scope}-${index}`,
      id: item.id,
      text: item.text || '',
      category: item.category === undefined
        ? (scope === 'global' ? null : scope)
        : item.category,
      source: item.source || 'manual',
    })));
    setExampleDraft('');
    setExamplesTouched(false);
    setExamplesOpen((existing?.examples || []).length > 0);
    setExampleReject(null);
    setSaveWarnings([]);
  };

  const closeEditor = () => {
    if (analyzing || saving) return;
    setEditScope(null);
  };

  const handleAnalyze = async () => {
    const texts = splitExamples(exampleText);
    if (texts.length === 0 && styleFiles.length === 0) {
      showToast('분석할 예시나 지침을 붙여넣거나 파일로 올려주세요', 'warning');
      return;
    }
    if (!isPro && freeAnalyzeUsed >= freeAnalyzeLimit) {
      setShowProModal(true);
      return;
    }
    try {
      setAnalyzing(true);
      let data;
      if (styleFiles.length > 0) {
        data = await analyzeStyleFiles(editScope, styleFiles, exampleText.trim());
        setExtractedTexts(data?.source_texts || null);
      } else {
        data = await analyzeStyleSources(editScope, texts);
        setExtractedTexts(null);
      }
      // 한도 초과·추출 실패 등 서버 안내를 그대로 보여준다
      (data?.warnings || []).forEach((warning) => showToast(warning, 'warning', 6000));
      const spec = data?.draft?.style_spec || {};
      setDraft({ ...EMPTY_DRAFT, ...spec });
      setDraftRules(data?.draft?.rule_summary || []);
      setAnalysisModel(data?.draft?.analysis_model || '');
      if (!isPro) setFreeAnalyzeUsed((n) => n + 1);
      showToast('분석 결과로 아래 항목을 채웠어요. 확인·수정 후 저장해주세요', 'success');
    } catch (error) {
      const message = String(error?.message || '');
      if (message.includes('PRO')) {
        setShowProModal(true);
      } else if (message.includes('추출하지 못했')) {
        showToast('파일에서 텍스트를 추출하지 못했어요. 내용을 직접 붙여넣어주세요', 'error');
      } else {
        showToast('스타일 분석에 실패했어요. 다시 시도해주세요', 'error');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const hasAnyStyleInput = (itemsToSave = exampleItems, touched = examplesTouched) =>
    Boolean(
      (instructions || '').trim()
      || (draft.tone_style || '').trim()
      || (draft.structure_flow || '').trim()
      || (draft.fixed_phrases || []).length > 0
      || draftRules.length > 0
      || itemsToSave.length > 0
      || (touched && profiles[editScope])
    );

  const ingestExampleDraft = (raw) => {
    const nextItems = createStyleExampleItems(raw, editScope);
    if (nextItems.length === 0) return;
    setExamplesTouched(true);
    setExampleReject(null);
    setExampleItems((cur) => [...cur, ...nextItems]);
    setExampleDraft('');
    setExamplesOpen(true);
  };

  const handleExamplePaste = (event) => {
    const pasted = event.clipboardData?.getData('text');
    if (!String(pasted || '').trim()) return;
    event.preventDefault();
    ingestExampleDraft(pasted);
  };

  // Enter 또는 '추가' 버튼 — 명시적 동작으로만 목록에 넣는다.
  // (blur 시점 자동 추가는 저장 버튼을 누르는 순간 목록이 늘어나 버튼이 밀리고 클릭이 사라지는 원인이었다)
  const handleExampleDraftSubmit = () => {
    if (exampleDraft.trim()) ingestExampleDraft(exampleDraft);
  };

  const handleRemoveExample = (localKey) => {
    setExamplesTouched(true);
    setExampleReject(null);
    setExampleItems((cur) => cur.filter((item) => item.localKey !== localKey));
  };

  const handleExampleItemChange = (localKey, nextText) => {
    setExamplesTouched(true);
    setExampleReject(null);
    setExampleItems((cur) => cur.map((item) => (
      item.localKey === localKey ? { ...item, text: nextText } : item
    )));
  };

  const handleSave = async () => {
    // 입력창에 남아 있는 예시(아직 Enter/추가를 누르지 않은 것)도 저장에 포함한다
    const { items: itemsToSave, pendingCount } = mergePendingStyleExampleDraft({
      items: exampleItems,
      draft: exampleDraft,
      editScope,
    });
    const touched = examplesTouched || pendingCount > 0;
    if (pendingCount > 0) {
      setExampleItems(itemsToSave);
      setExampleDraft('');
      setExamplesTouched(true);
      setExamplesOpen(true);
    }
    if (!hasAnyStyleInput(itemsToSave, touched)) {
      showToast('문체·고정 문구·규칙·지시 중 하나는 입력해주세요', 'warning');
      return;
    }
    if (!canEditScope(editScope)) {
      setShowProModal(true);
      return;
    }
    try {
      setSaving(true);
      setExampleReject(null);
      const payload = buildStyleProfileSavePayload({
        instructions,
        styleSpec: draft,
        ruleSummary: draftRules,
        sourceTexts: extractedTexts || splitExamples(exampleText),
        analysisModel,
        examplesTouched: touched,
        exampleItems: itemsToSave,
      });
      const data = await upsertStyleProfile(editScope, payload);
      trackSaenggibuUxEvent('saenggibu_style_saved', {
        saenggibu_category: editScope,
        is_batch: false,
      });
      const warnings = Array.isArray(data?.warnings) ? data.warnings.filter(Boolean) : [];
      setSaveWarnings(warnings);
      showToast('내 스타일을 저장했어요. 이제 생성에 자동 반영돼요', 'success');
      await loadProfiles();
      if (warnings.length === 0) {
        setEditScope(null);
      } else if (data?.profile?.examples) {
        setExampleItems((data.profile.examples || []).map((item, index) => ({
          localKey: item.id || `saved-${editScope}-${index}`,
          id: item.id,
          text: item.text || '',
          category: item.category === undefined
            ? (editScope === 'global' ? null : editScope)
            : item.category,
          source: item.source || 'manual',
        })));
        setExamplesTouched(false);
      }
    } catch (error) {
      const rejection = styleExampleRejectionFromError(error);
      if (rejection) {
        setExampleReject(rejection);
        setExamplesOpen(true);
        return;
      }
      const message = String(error?.detail || error?.message || '');
      if (message.includes('PRO')) {
        setShowProModal(true);
      } else {
        showToast('저장하지 못했어요. 다시 시도해주세요', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleMasterToggle = async (nextEnabled) => {
    const prev = masterEnabled;
    setMasterToggling(true);
    // 낙관적 업데이트
    setMasterEnabled(nextEnabled);
    try {
      await setStyleMasterEnabled(nextEnabled);
      showToast(nextEnabled ? '내 스타일을 다시 적용해요' : '모든 생성에서 내 스타일을 껐어요', 'success');
    } catch (error) {
      setMasterEnabled(prev);
      showToast('변경하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setMasterToggling(false);
    }
  };

  const handleToggle = async (scope, nextEnabled) => {
    if (!canEditScope(scope)) {
      setShowProModal(true);
      return;
    }
    const prev = profiles;
    setTogglingScope(scope);
    // 낙관적 업데이트
    setProfiles((cur) => ({
      ...cur,
      [scope]: { ...cur[scope], enabled: nextEnabled },
    }));
    try {
      await upsertStyleProfile(scope, { enabled: nextEnabled });
      showToast(nextEnabled ? '스타일을 켰어요' : '스타일을 껐어요', 'success');
    } catch (error) {
      setProfiles(prev);
      showToast('변경하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setTogglingScope(null);
    }
  };

  const handleDelete = async (scope) => {
    const confirmed = await requestConfirm({
      title: '이 스타일을 삭제할까요?',
      description: '삭제한 스타일은 되돌릴 수 없어요',
      confirmLabel: '삭제하기',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await deleteStyleProfile(scope);
      showToast('스타일을 삭제했어요', 'success');
      setEditScope(null);
      await loadProfiles();
    } catch (error) {
      showToast('삭제하지 못했어요. 다시 시도해주세요', 'error');
    }
  };

  // 예전에 쓴 생기부를 붙여 넣으면 분석 → 공통 스타일 저장 → 적용까지 한 번에 끝낸다.
  const handleQuickApply = async () => {
    const texts = splitExamples(quickText);
    if (texts.length === 0) {
      showToast('예전에 쓴 생기부를 붙여넣어 주세요', 'warning');
      return;
    }
    if (!isPro && freeAnalyzeUsed >= freeAnalyzeLimit) {
      setShowProModal(true);
      return;
    }
    try {
      setQuickSaving(true);
      const data = await analyzeStyleSources('global', texts);
      if (!isPro) setFreeAnalyzeUsed((n) => n + 1);
      const existing = profiles.global || {};
      await upsertStyleProfile('global', {
        ...buildStyleProfileSavePayload({
          instructions: existing.instructions || '',
          styleSpec: { ...EMPTY_DRAFT, ...(data?.draft?.style_spec || {}) },
          ruleSummary: [...(existing.rule_summary || []), ...(data?.draft?.rule_summary || [])],
          sourceTexts: texts,
          analysisModel: data?.draft?.analysis_model || '',
          examplesTouched: false,
          exampleItems: [],
        }),
        enabled: true,
      });
      if (!masterEnabled) {
        await setStyleMasterEnabled(true);
        setMasterEnabled(true);
      }
      trackSaenggibuUxEvent('saenggibu_style_quick_applied', {
        saenggibu_category: 'global',
        is_batch: false,
        is_pro: isPro,
      });
      setQuickText('');
      showToast('내 문체를 저장했어요. 다음 생성부터 이 문체로 써요', 'success');
      await loadProfiles();
    } catch (error) {
      const message = String(error?.detail || error?.message || '');
      if (message.includes('PRO')) {
        setShowProModal(true);
      } else {
        showToast('문체를 읽지 못했어요. 다시 시도해주세요', 'error');
      }
    } finally {
      setQuickSaving(false);
    }
  };

  const updateDraftField = (key, value) => {
    setDraft((cur) => ({ ...cur, [key]: value }));
  };

  const updateDraftList = (key, raw) => {
    const items = String(raw || '')
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    setDraft((cur) => ({ ...cur, [key]: items }));
  };

  const editLabel = editScope ? SCOPE_LABELS[editScope] : '';
  const hasAnyProfile = useMemo(() => Object.keys(profiles).length > 0, [profiles]);
  const usageLine = formatSaenggibuUsageLine({
    ...(usageSummary || {}),
    hasProfile: hasAnyProfile,
  });
  const examplesLimit = isPro
    ? getStyleExamplesLimit(editScope, profiles[editScope]?.examples_limit)
    : FREE_STYLE_EXAMPLE_LIMIT;
  const hasGlobalStyle = hasStyleProfileContent(profiles.global);

  const renderCard = (scope) => {
    const profile = profiles[scope];
    const summary = profileSummary(profile);
    const isGlobal = scope === 'global';
    return (
      <div
        key={scope}
        className={`mystyle-card ${isGlobal ? 'mystyle-card-global' : ''}`}
        onClick={() => openEditor(scope)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && openEditor(scope)}
      >
        <div className="mystyle-card-head">
          <div>
            <div className="mystyle-card-title">{SCOPE_LABELS[scope]}</div>
            <div className="mystyle-card-desc">
              {isGlobal
                ? '학교·교육청 지침처럼 모든 생기부에 적용할 설정'
                : summary || '설정 안 함'}
            </div>
          </div>
          {profile ? (
            <div
              className="mystyle-toggle-area"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <label className="mystyle-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(profile.enabled)}
                  disabled={togglingScope === scope}
                  onChange={(e) => handleToggle(scope, e.target.checked)}
                />
                <span className="mystyle-toggle-slider" />
              </label>
              <span
                className={`mystyle-toggle-caption ${profile.enabled ? 'on' : ''}`}
              >
                {profile.enabled ? '적용 중' : '꺼짐'}
              </span>
            </div>
          ) : (
            <span className="mystyle-card-cta">{canEditScope(scope) ? '설정하기' : 'PRO'}</span>
          )}
        </div>
        {isGlobal && summary && <div className="mystyle-card-summary">{summary}</div>}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>내 스타일 | 생기부 작성 스타일 설정 | 왓퀴즈</title>
        <meta
          name="description"
          content="우수 기재 예시와 학교·교육청 지침을 한 번 등록하면 모든 생기부 생성에 자동 반영돼요."
        />
      </Helmet>

      <div className="mystyle-page">
        <div className="mystyle-container">
          <div className="mystyle-header">
            <h1>내 스타일</h1>
            <p>우수 예시와 지침을 한 번 등록하면 모든 생기부 생성에 자동 반영돼요</p>
            {usageLine ? <p className="mystyle-usage-line">{usageLine}</p> : null}
          </div>

          {!currentUser ? (
            <div className="mystyle-empty">
              <div className="mystyle-empty-title">
                우리 학교 스타일 그대로 생기부가 나와요
              </div>
              <div className="mystyle-empty-desc">
                잘 쓴 예시를 등록하면 AI가 문체와 형식을 학습해서
                <br />
                매번 지시하지 않아도 그 스타일로 작성해드려요
              </div>
              <div className="mystyle-empty-demo">
                <StyleApplyDemo surface="my_style" />
              </div>
              <button
                className="mystyle-primary-btn"
                onClick={() => setShowLoginPrompt(true)}
              >
                로그인하고 시작하기
              </button>
            </div>
          ) : loading ? (
            <div className="mystyle-skeleton-list">
              {[0, 1, 2].map((i) => (
                <div key={i} className="mystyle-skeleton-card" />
              ))}
            </div>
          ) : (
            <>
              {hasAnyProfile && (
                <div className="mystyle-master-row">
                  <div>
                    <div className="mystyle-master-title">전체 적용</div>
                    <div className="mystyle-master-caption">
                      끄면 모든 생성에서 내 스타일이 잠시 적용되지 않아요
                    </div>
                  </div>
                  <label className="mystyle-toggle">
                    <input
                      type="checkbox"
                      checked={masterEnabled}
                      disabled={masterToggling}
                      onChange={(e) => handleMasterToggle(e.target.checked)}
                    />
                    <span className="mystyle-toggle-slider" />
                  </label>
                </div>
              )}
              {!hasGlobalStyle && (
                <div className="mystyle-quick" data-testid="mystyle-quick">
                  <div className="mystyle-quick-title">예전에 쓴 생기부를 붙여넣어 주세요</div>
                  <div className="mystyle-quick-sub">문체를 읽어서 다음 생성부터 바로 적용해요 · 학생 이름은 지워주세요</div>
                  <textarea
                    className="mystyle-textarea"
                    rows={5}
                    value={quickText}
                    onChange={(e) => setQuickText(e.target.value)}
                    placeholder="잘 썼다고 생각하는 생기부를 2~3개 붙여넣으세요 (여러 개는 빈 줄 두 번으로 구분)"
                    aria-label="예전에 쓴 생기부"
                  />
                  <button
                    className="mystyle-primary-btn"
                    onClick={handleQuickApply}
                    disabled={quickSaving || !quickText.trim()}
                  >
                    {quickSaving ? '문체를 읽고 있어요…' : '내 스타일로 바로 적용'}
                  </button>
                </div>
              )}
              {!isPro && (
                <div className="mystyle-pro-hint">
                  공통 스타일 1개는 무료로 저장해 모든 생성에 쓸 수 있어요. 종류별 설정은 PRO에서 가능해요.
                </div>
              )}
              {renderCard('global')}
              <div className="mystyle-section-label">생기부 종류별 설정</div>
              <div className="mystyle-grid">
                {CATEGORY_SCOPES.map((scope) => renderCard(scope))}
              </div>
              {!hasAnyProfile && (
                <div className="mystyle-footnote">
                  카드를 눌러 잘 쓴 예시를 붙여넣으면, AI가 어떻게 이해했는지 먼저
                  보여드려요
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 편집 모달 (모바일: 풀 바텀시트)
          바깥 클릭으로 닫지 않는다 — 붙여넣은 예시/지시 입력이 유실되는 것을 방지.
          닫기는 X 버튼으로만 (드래그 선택 후 바깥에서 마우스를 떼도 안전) */}
      {editScope && (
        <div className="mystyle-modal-overlay">
          <div className="mystyle-modal">
            <div className="mystyle-modal-head">
              <div className="mystyle-modal-title">{editLabel} 스타일</div>
              <button className="mystyle-modal-close" onClick={closeEditor} aria-label="닫기">
                ✕
              </button>
            </div>

            <div className="mystyle-modal-body">
              {/* AI 자동 채우기 (선택적 도우미) — 직접 입력이 기본. 박스 전체가 드롭 영역 */}
              <div
                className={`mystyle-ai-assist ${isDragOver ? 'dragover' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragOver(false);
                  addStyleFiles(e.dataTransfer?.files);
                }}
              >
                <div className="mystyle-ai-assist-title">AI로 자동 채우기 (선택)</div>
                <div className="mystyle-ai-assist-sub">
                  잘 쓴 예시나 지침을 붙여넣거나 파일을 끌어다 놓으면 아래 항목을
                  채워드려요 · 학생 이름은 지워주세요
                </div>
                <textarea
                  className="mystyle-textarea"
                  rows={4}
                  value={exampleText}
                  onChange={(e) => setExampleText(e.target.value)}
                  placeholder={
                    editScope === 'global'
                      ? '학교나 교육청의 기재 지침을 붙여넣으세요'
                      : '잘 썼다고 생각하는 생기부 예시를 붙여넣으세요 (여러 개는 빈 줄 두 번으로 구분)'
                  }
                />
                <div className="mystyle-file-row">
                  <label className="mystyle-file-attach">
                    파일로 올리기 (PDF, HWP, 이미지)
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.hwp,.hwpx,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.heic,.heif"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        addStyleFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {styleFiles.map((file, index) => (
                    <span key={`${file.name}-${index}`} className="mystyle-file-chip">
                      {file.name.length > 24 ? `${file.name.slice(0, 24)}…` : file.name}
                      <button
                        aria-label="파일 제거"
                        onClick={() =>
                          setStyleFiles((cur) => cur.filter((_, i) => i !== index))
                        }
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
                <button
                  className="mystyle-assist-btn"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                >
                  {analyzing ? 'AI가 분석하고 있어요…' : 'AI 분석으로 아래 항목 채우기'}
                </button>
              </div>

              <label className="mystyle-field-label">
                문체
                <span className="mystyle-field-sub">예: 관찰 중심의 담백한 문체, 과한 수식어 지양</span>
              </label>
              <textarea
                className="mystyle-textarea mystyle-textarea-small"
                rows={2}
                value={draft.tone_style || ''}
                onChange={(e) => updateDraftField('tone_style', e.target.value)}
                placeholder="원하는 문체를 적어주세요"
              />
              <label className="mystyle-field-label">
                고정 문구·형식
                <span className="mystyle-field-sub">한 줄에 하나씩 · 항상 그대로 지켜요</span>
              </label>
              <textarea
                className="mystyle-textarea mystyle-textarea-small"
                rows={2}
                value={(draft.fixed_phrases || []).join('\n')}
                onChange={(e) => updateDraftList('fixed_phrases', e.target.value)}
                placeholder="예: 첫 문장은 도서명을 밝히며 '~을 탐독함.'으로 시작"
              />
              <label className="mystyle-field-label">
                서술 흐름
                <span className="mystyle-field-sub">예: 동기 → 탐구 과정 → 결과 → 확장</span>
              </label>
              <textarea
                className="mystyle-textarea mystyle-textarea-small"
                rows={2}
                value={draft.structure_flow || ''}
                onChange={(e) => updateDraftField('structure_flow', e.target.value)}
                placeholder="문장들이 어떤 순서로 전개되면 좋을지 적어주세요"
              />
              <label className="mystyle-field-label">
                규칙
                <span className="mystyle-field-sub">한 줄에 하나씩 · 지침 문서에서 자동 추출돼요</span>
              </label>
              <textarea
                className="mystyle-textarea mystyle-textarea-small"
                rows={2}
                value={draftRules.join('\n')}
                onChange={(e) =>
                  setDraftRules(
                    String(e.target.value || '')
                      .split('\n')
                      .map((t) => t.trim())
                      .filter(Boolean)
                  )
                }
                placeholder="예: 도서명은 작은따옴표로 표기한다"
              />
              <label className="mystyle-field-label">
                자유 지시
                <span className="mystyle-field-sub">항상 지켜줬으면 하는 요청 (최대 3,000자)</span>
              </label>
              <textarea
                className="mystyle-textarea mystyle-textarea-small"
                rows={3}
                maxLength={3000}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="예: 독서 활동을 먼저, 발표 활동을 나중에 서술"
              />
              <StyleExamplesSection
                isPro={canEditScope(editScope)}
                limit={examplesLimit}
                items={exampleItems}
                draft={exampleDraft}
                open={examplesOpen}
                reject={exampleReject}
                warnings={saveWarnings}
                onToggle={() => setExamplesOpen((open) => !open)}
                onDraftChange={setExampleDraft}
                onDraftSubmit={handleExampleDraftSubmit}
                onPaste={handleExamplePaste}
                onItemChange={handleExampleItemChange}
                onRemove={handleRemoveExample}
              />
              <div className="mystyle-modal-actions">
                <button
                  className="mystyle-primary-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? '저장하고 있어요…' : '이대로 저장하기'}
                </button>
                {profiles[editScope] && (
                  <div className="mystyle-secondary-actions">
                    <button
                      className="mystyle-ghost-btn mystyle-danger"
                      onClick={() => handleDelete(editScope)}
                    >
                      삭제하기
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <LoginPrompt
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature="saenggibu"
      />
      {showProModal && (
        <ProUpgradeModal
          isOpen={showProModal}
          onClose={() => setShowProModal(false)}
          feature="내 스타일 (우리 학교 스타일 학습)"
          description="우수 예시와 지침을 등록하면 모든 생기부가 우리 학교 스타일로 나와요. 매번 지시하지 않아도 돼요."
        />
      )}
    </>
  );
};

export default MyStylePage;
